import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import { useTheme } from './useTheme';

// Notice we added the 'profile' prop here so we can hydrate the form!
export default function SettingsView({ profile, showToast, setActiveModal, defaultTab = 'privacy' }) {
  const { user } = useContext(AuthContext); // To get the user's email
  const [activeTab, setActiveTab] = useState(defaultTab); // 🛠️ CHANGE THIS

  const [theme, setTheme] = useTheme();

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // 🆕 ADD THIS EFFECT: It forces the tab to switch if the defaultTab prop changes
  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  // 1. Form State matching your backend controller expectations
  const [formData, setFormData] = useState({
    display_name: '',
    handle: '',
    bio: '',
    location_text: '',
    country_code: '',
    default_comments_state: 'Enabled'
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // 2. New state for avatar upload
  const fileInputRef = useRef(null); // 3. Ref to trigger hidden file input

  const [selectedTags, setSelectedTags] = useState([]); // Array of active tags (chips)
  const [dropdownValue, setDropdownValue] = useState(''); // Value of the active dropdown selection
  const [customTagInput, setCustomTagInput] = useState(''); // Text in the custom text field
  const [showCustomInput, setShowCustomInput] = useState(false); // Controls text-field visibility
  const [isSavingTags, setIsSavingTags] = useState(false);

  // NEW: Holds the master list of disciplines loaded from your database
  const [availableTypes, setAvailableTypes] = useState([]);

  // New state for portfolio highlight input
  const [highlightInput, setHighlightInput] = useState({ platform: 'youtube', url: '' });
  const [isSavingHighlight, setIsSavingHighlight] = useState(false);

  const [platforms, setPlatforms] = useState({
    instagram: { url: '', followers: '' },
    youtube: { url: '', followers: '' },
    behance: { url: '', followers: '' },
    dribbble: { url: '', followers: '' },
    spotify: { url: '', followers: '' },
    twitter: { url: '', followers: '' }
  });
  const [isSavingPlatforms, setIsSavingPlatforms] = useState(false);

  // States for Press and Awards
  const [pressData, setPressData] = useState({ title: '', publisher: '', publish_date: '', link_url: '' });
  const [awardData, setAwardData] = useState({ award_title: '', organization: '', award_year: '', award_tier: '' });
  const [isSavingPress, setIsSavingPress] = useState(false);
  const [isSavingAward, setIsSavingAward] = useState(false);

  // States for Upcoming Events
  const [eventData, setEventData] = useState({ event_name: '', event_date: '', location: '', is_free: true, ticket_url: '' });
  const [isSavingEvent, setIsSavingEvent] = useState(false);


    // 2. Hydrate the form when the profile prop arrives
  useEffect(() => {
    if (profile) {
      setFormData({
        display_name: profile.display_name || '',
        handle: profile.handle || '',
        bio: profile.bio || '',
        location_text: profile.location_text || '',
        country_code: profile.country_code || '',
        default_comments_state: profile.default_comments_state || 'Enabled'
      });

      // Populate active tag chips directly as an array
      if (profile.creative_types && Array.isArray(profile.creative_types)) {
        setSelectedTags(profile.creative_types);
      }
    }
  }, [profile]);

  // Fetch master creative disciplines directly from the database table
  useEffect(() => {
    const fetchMasterTypes = async () => {
      try {
        // Point to our new master category endpoint
        const res = await api.get('/portfolio/categories'); 
        
        // Grab the flat array of strings sent by the controller
        if (res.data && res.data.categories) {
          setAvailableTypes(res.data.categories);
        }
      } catch (err) {
        console.error("Failed to load master creative types from database:", err);
        // Safe fallback just in case the database call fails
        setAvailableTypes(['Designers', 'Musicians', 'Photographers', 'Actors', 'Writers']);
      }
    };

    fetchMasterTypes();
  }, []);

  useEffect(() => {
    if (activeTab === 'platforms') { 
      const fetchLinks = async () => {
        try {
          const res = await api.get('/portfolio/links');
          const data = res.data;
          const rawLinks = Array.isArray(data) ? data : (data?.links || []);

          const linksObj = { 
            instagram: { url: '', followers: '' }, 
            youtube: { url: '', followers: '' }, 
            behance: { url: '', followers: '' }, 
            dribbble: { url: '', followers: '' }, 
            spotify: { url: '', followers: '' }, 
            twitter: { url: '', followers: '' } 
          };
          
          if (Array.isArray(rawLinks)) {
            rawLinks.forEach(link => {
              const p = (link.platform || link.platform_name || '').toLowerCase();
              if (p in linksObj) {
                linksObj[p] = {
                  url: link.profile_url || link.url || '',
                  followers: link.follower_count || ''
                };
              }
            });
          }

          setPlatforms(linksObj);
        } catch (err) {
          console.error("Error fetching portfolio links:", err);
        }
      };
      fetchLinks();
    }
  }, [activeTab]);

 
  const navItems = [
    { id: 'privacy', icon: '🔒', label: 'Privacy' },
    { id: 'account', icon: '👤', label: 'Account' },
    { id: 'notifications', icon: '🔔', label: 'Notifications' },
    { id: 'content', icon: '📄', label: 'Content' },
    { id: 'platforms', icon: '🔗', label: 'Platforms' }, // <-- NEW TAB ADDED HERE
    { id: 'blocked', icon: '🚫', label: 'Blocked users' },
    { id: 'data', icon: '📊', label: 'Data & Privacy' },
    { id: 'appearance', icon: '🎨', label: 'Appearance' },
    { id: 'about', icon: 'ℹ️', label: 'About' }
  ];

  const activeNavItem = navItems.find((item) => item.id === activeTab) || navItems[0];

  // 3. Unified Save function that hits both endpoints at once
  const handleSaveProfile = async () => {
    if (!formData.display_name || !formData.handle) {
      showToast('Display name and handle are required.');
      return;
    }
    
    setIsSaving(true);
    try {
      // Create both API requests
      const profileRequest = api.post('/profiles/me', formData);
      const tagsRequest = api.post('/portfolio/types', { types: selectedTags });

      // Wait for BOTH requests to finish successfully
      await Promise.all([profileRequest, tagsRequest]);

      showToast('Profile and disciplines saved successfully! ✓');
      
      // Reload to ensure the topbar and dashboard states grab the fresh data
      setTimeout(() => window.location.reload(), 1500); 
    } catch (err) {
      console.error("Save profile error:", err);
      showToast(err.response?.data?.message || 'Error saving profile.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAutoSave = async (field, value) => {
    const previousValue = formData[field];
    const updatedData = { ...formData, [field]: value };
  
    // 1. Optimistic UI update
    setFormData(updatedData);
  
    // 2. Persist directly to backend
    try {
      await api.post('/profiles/me', updatedData);
      showToast('Setting updated');
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
      
      // Revert state back to original value if API request fails
      setFormData((prev) => ({ ...prev, [field]: previousValue }));
      showToast('Failed to save setting');
    }
  };

  // 4. The Avatar Upload Handler
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showToast('Please select a valid image (JPG, PNG, WEBP).');
      return;
    }

    // Wrap file in FormData (Required for Multer)
    const uploadData = new FormData();
    uploadData.append('avatar', file); // 'avatar' matches your upload.single('avatar') in Express

    setIsUploading(true);
    try {
      // Ensure we pass multipart/form-data headers so Axios doesn't send JSON
      const res = await api.post('/uploads/avatar', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      showToast('Avatar uploaded successfully! 📸');
      // Reload to pull fresh avatar into Topbar, Feed, and Profile views
      setTimeout(() => window.location.reload(), 1500); 
    } catch (err) {
      console.error("Upload error:", err);
      showToast('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset input so user can select the same file again if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveLinks = async () => {
    setIsSavingPlatforms(true);
    try {
      // 1. Safely check that the platform object exists AND has a URL before trimming
      const filledPlatforms = Object.keys(platforms).filter(key => {
        const platformData = platforms[key];
        return platformData && platformData.url && platformData.url.trim() !== '';
      });

      // 2. Map them into individual Axios POST requests
      const uploadPromises = filledPlatforms.map(key => {
        return api.post('/portfolio/links', {
          platform: key,
          profile_url: platforms[key].url.trim(),
          follower_count: parseInt(platforms[key].followers) || 0
        });
      });

      // 3. Wait for all the individual upserts to complete
      await Promise.all(uploadPromises);

      showToast('Platform links updated successfully! 🔗');
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      console.error("Error saving portfolio links:", err);
      showToast(err.response?.data?.message || 'Failed to update platform links.');
    } finally {
      setIsSavingPlatforms(false);
    }
  };

// Adds a tag to the chips array (preventing duplicates)
const handleAddTag = (tagToAdd) => {
  const cleanTag = tagToAdd.trim();
  if (cleanTag && !selectedTags.includes(cleanTag)) {
    setSelectedTags([...selectedTags, cleanTag]);
  }
};

// Removes a tag when clicking the "X" button on the chip
const handleRemoveTag = (tagToRemove) => {
  setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
};

// Triggers when the dropdown select is changed
const handleDropdownChange = (e) => {
  const val = e.target.value;
  setDropdownValue(val);

  if (val === 'custom') {
    setShowCustomInput(true);
  } else if (val) {
    handleAddTag(val);
    setDropdownValue(''); // Reset dropdown back to placeholder
    setShowCustomInput(false);
  }
};

// Confirms adding a typed custom tag to the list
const handleAddCustomTagSubmit = (e) => {
  e.preventDefault();
  if (customTagInput.trim()) {
    handleAddTag(customTagInput.trim());
    setCustomTagInput('');
    setShowCustomInput(false);
    setDropdownValue(''); // Reset dropdown selection
  }
};

// Saves the final tag array to your database
const handleSaveTags = async () => {
  setIsSavingTags(true);
  try {
    // Sends array directly: { types: ['Designers', 'My Custom Tag'] }
    await api.post('/portfolio/types', { types: selectedTags });
    
    showToast('Creative tags updated successfully! 🏷️');
    setTimeout(() => window.location.reload(), 1500);
  } catch (err) {
    console.error("Error saving tags:", err);
    showToast(err.response?.data?.message || 'Failed to update creative tags.');
  } finally {
    setIsSavingTags(false);
  }
};

const handleAddHighlight = async () => {
  if (!highlightInput.url) return showToast('Please enter a URL first.');
  
  // Nifty Regex to check if the URL starts with http:// or https://
  let processedUrl = highlightInput.url.trim();
  if (!/^https?:\/\//i.test(processedUrl)) {
    processedUrl = 'https://' + processedUrl; // Auto-append it!
  }

  setIsSavingHighlight(true);
  try {
    await api.post('/portfolio/highlights', {
      platform: highlightInput.platform,
      link_url: processedUrl // Use the processed URL here
    });
    
    showToast('Portfolio highlight added! Thumbnail generated.');
    setTimeout(() => window.location.reload(), 1500);
  } catch (err) {
    console.error("Highlight Error:", err);
    showToast(err.response?.data?.message || 'Failed to add highlight.');
  } finally {
    setIsSavingHighlight(false);
  }
};

// Handlers FOR PRESS & AWARDS
const handleAddPress = async (e) => {
  e.preventDefault();
  if (!pressData.title || !pressData.link_url) return showToast('Title and URL are required');
  
  // Auto-fix URL just like we did for portfolio highlights
  let processedUrl = pressData.link_url.trim();
  if (!/^https?:\/\//i.test(processedUrl)) processedUrl = 'https://' + processedUrl;

  setIsSavingPress(true);
  try {
    await api.post('/profiles/press', { ...pressData, link_url: processedUrl });
    showToast('Press article added successfully!');
    setPressData({ title: '', publisher: '', publish_date: '', link_url: '' });
    setTimeout(() => window.location.reload(), 1500); // Quick refresh to show data
  } catch (err) {
    showToast('Error adding press article.');
  } finally {
    setIsSavingPress(false);
  }
};

const handleAddAward = async (e) => {
  e.preventDefault();
  if (!awardData.award_title || !awardData.organization) return showToast('Title and Organization are required');
  setIsSavingAward(true);
  try {
    await api.post('/profiles/awards', awardData);
    showToast('Award added successfully!');
    setAwardData({ award_title: '', organization: '', award_year: '', award_tier: '' });
    setTimeout(() => window.location.reload(), 1500); // Quick refresh to show data
  } catch (err) {
    showToast('Error adding award.');
  } finally {
    setIsSavingAward(false);
  }
};

// Handler FOR UPCOMING EVENTS
const handleAddEvent = async (e) => {
  e.preventDefault();
  if (!eventData.event_name || !eventData.event_date || !eventData.location) {
    return showToast('Name, Date, and Location are required');
  }

  // Auto-fix URL if provided
  let processedUrl = eventData.ticket_url.trim();
  if (processedUrl && !/^https?:\/\//i.test(processedUrl)) processedUrl = 'https://' + processedUrl;

  setIsSavingEvent(true);
  try {
    await api.post('/profiles/events', { ...eventData, ticket_url: processedUrl });
    showToast('Event added successfully!');
    setEventData({ event_name: '', event_date: '', location: '', is_free: true, ticket_url: '' });
    setTimeout(() => window.location.reload(), 1500);
  } catch (err) {
    showToast('Error adding event.');
  } finally {
    setIsSavingEvent(false);
  }
};


  return (
    <div className="view active" id="view-settings">
      <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', fontFamily: "'Syne', sans-serif", marginBottom: '1rem' }}>
        Settings
      </div>
      
        <div className="settings-layout">
    {/* 🆕 MOBILE MENU TOGGLE BUTTON (Only displays on small screens) */}
    <button 
      className="settings-mobile-toggle" 
      onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {activeNavItem.icon} {activeNavItem.label}
      </span>
      <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
        {isMobileNavOpen ? '▲' : '▼'}
      </span>
    </button>

    {/* SETTINGS NAVIGATION LIST */}
    <div className={`settings-nav ${isMobileNavOpen ? 'mobile-open' : ''}`}>
      {navItems.map((item) => (
        <div 
          key={item.id}
          className={`sn-item ${activeTab === item.id ? 'active' : ''}`} 
          onClick={() => {
            setActiveTab(item.id);
            setIsMobileNavOpen(false); // 👈 Auto-close menu on selection on mobile
          }}
        >
          {item.icon} {item.label}
        </div>
      ))}
    </div>
        
        <div className="settings-panel">

          {/* PRIVACY SETTINGS (Truncated for brevity, leave yours exactly as is) */}
          {/* PRIVACY SETTINGS */}
          {activeTab === 'privacy' && (
            <div className="settings-section active" id="settings-privacy">
              <div className="ss-title">Privacy settings</div>
              <div className="ss-sub">Control who can see your profile and interact with you.</div>
              
              {/* Original Privacy Settings */}
              <div className="setting-row">
                <div className="sr-info">
                  <div className="sr-label">Profile visibility</div>
                  <div className="sr-desc">Who can view your full profile</div>
                </div>
                <select className="setting-select" defaultValue="Everyone">
                  <option>Everyone</option>
                  <option>Connections only</option>
                  <option>Nobody (private)</option>
                </select>
              </div>
              
              <div className="setting-row">
                <div className="sr-info">
                  <div className="sr-label">Show in Discover</div>
                  <div className="sr-desc">Appear in talent scout searches and Discover feed</div>
                </div>
                <label className="toggle"><input type="checkbox" defaultChecked /><span className="toggle-slider"></span></label>
              </div>
              
              <div className="setting-row">
                <div className="sr-info">
                  <div className="sr-label">Allow connection requests</div>
                  <div className="sr-desc">Let others send you connection requests</div>
                </div>
                <label className="toggle"><input type="checkbox" defaultChecked /><span className="toggle-slider"></span></label>
              </div>

              {/* --- MOVED FROM CONTENT TAB --- */}
              <div className="setting-row" style={{ marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '20px' }}>
                <div className="sr-info"><div className="sr-label">Default post audience</div></div>
                <select className="setting-select" defaultValue="Everyone">
                  <option>Everyone</option>
                  <option>Connections only</option>
                  <option>Followers only</option>
                </select>
              </div>

              {/* Allow Reposts Toggle */}
              <div className="setting-row">
                <div className="sr-info"><div className="sr-label">Allow reposts of my content</div></div>
                <label className="toggle">
                  <input 
                    type="checkbox" 
                    Checked={formData.allow_reposts ?? true}
                    onChange={(e) => handleAutoSave('allow_reposts', e.target.checked)} 
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
                        
              <div className="setting-row">
                <div className="sr-info"><div className="sr-label">Show vote counts</div></div>
                <label className="toggle"><input type="checkbox" defaultChecked /><span className="toggle-slider"></span></label>
              </div>
              
              <div className="setting-row">
                <div className="sr-info">
                  <div className="sr-label">Sensitive content filter</div>
                  <div className="sr-desc">Filter potentially sensitive content from your feed</div>
                </div>
                <label className="toggle"><input type="checkbox" defaultChecked /><span className="toggle-slider"></span></label>
              </div>
              
              <div className="setting-row">
                <div className="sr-info">
                  <div className="sr-label">Language filter</div>
                  <div className="sr-desc">Show content primarily in your language</div>
                </div>
                <label className="toggle"><input type="checkbox" /><span className="toggle-slider"></span></label>
              </div>

            </div>
          )}

          {/* ACCOUNT SETTINGS - NOW CONNECTED TO DATABASE */}
          {activeTab === 'account' && (
            <div className="settings-section active" id="settings-account">
              <div className="ss-title">Account settings</div>
              <div className="ss-sub">Manage your personal details and public identity.</div>

              {/* 5. AVATAR UPLOAD UI */}
              <div className="avatar-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0 24px' }}>
                <div 
                  className="avatar-ring" 
                  style={{ width: '96px', height: '96px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--purple), var(--teal))', padding: '2.5px', marginBottom: '12px', cursor: 'pointer', position: 'relative' }}
                  onClick={() => !isUploading && fileInputRef.current.click()}
                >
                  <div className="avatar-inner" style={{ width: '100%', height: '100%', borderRadius: '50%', background: 'var(--card)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: '36px' }}>🎨</span>
                    )}
                  </div>
                  
                  {/* Loading Overlay */}
                  {isUploading && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: 600 }}>
                      Wait...
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text3)' }}>Tap to change avatar</div>
                
                {/* Hidden File Input */}
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  style={{ display: 'none' }} 
                  accept="image/png, image/jpeg, image/webp" 
                  onChange={handleAvatarUpload} 
                />
              </div>

              {/* Text Fields */}             
              <div className="setting-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                <div className="sr-info" style={{ width: '100%' }}>
                  <div className="sr-label">Display name</div>
                  <input 
                    type="text" 
                    className="setting-select" 
                    style={{ width: '100%', marginTop: '6px' }}
                    value={formData.display_name}
                    onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                  />
                </div>
              </div>

              <div className="setting-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                <div className="sr-info" style={{ width: '100%' }}>
                  <div className="sr-label">Handle</div>
                  <input 
                    type="text" 
                    className="setting-select" 
                    style={{ width: '100%', marginTop: '6px' }}
                    value={formData.handle}
                    onChange={(e) => setFormData({...formData, handle: e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, '')})}
                  />
                </div>
              </div>

              <div className="setting-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                <div className="sr-info" style={{ width: '100%' }}>
                  <div className="sr-label">Bio</div>
                  <textarea 
                    className="setting-select" 
                    style={{ width: '100%', marginTop: '6px', minHeight: '80px', resize: 'vertical' }}
                    value={formData.bio}
                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    placeholder="Tell the creative world about yourself..."
                  />
                </div>
              </div>

              {/* CREATIVE TAGS INPUT */}
              <div className="setting-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', marginTop: '16px' }}>
                <div className="sr-info" style={{ width: '100%' }}>
                  <div className="sr-label">Creative Disciplines</div>
                  <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '2px', marginBottom: '12px' }}>
                    Select pre-defined tags below, or type custom disciplines to build your list.
                  </div>

                  {/* ACTIVE TAG CHIPS DISPLAY */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    {selectedTags.map((tag) => (
                      <div 
                        key={tag} 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          background: 'rgba(155, 89, 182, 0.15)', // Faint theme purple
                          border: '1px solid var(--purple)',
                          borderRadius: '20px', 
                          padding: '4px 12px', 
                          fontSize: '13px', 
                          color: 'var(--text)',
                        }}
                      >
                        <span>{tag}</span>
                        <button 
                          type="button" 
                          onClick={() => handleRemoveTag(tag)}
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            color: 'var(--text3)', 
                            marginLeft: '8px', 
                            cursor: 'pointer',
                            fontSize: '14px',
                            lineHeight: '1',
                            display: 'flex',
                            alignItems: 'center',
                            padding: 0
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                    {selectedTags.length === 0 && (
                      <span style={{ fontSize: '13px', color: 'var(--text3)', fontStyle: 'italic' }}>
                        No disciplines selected. Use the selector below.
                      </span>
                    )}
                  </div>

                  {/* DISCIPLINE DROPDOWN */}
                  <select 
                    className="setting-select" 
                    style={{ width: '100%', marginTop: '6px' }}
                    value={dropdownValue}
                    onChange={handleDropdownChange}
                  >
                    <option value="" disabled>Choose a discipline to add...</option>
                    {['Designers', 'Musicians', 'Photographers', 'Actors', 'Writers'].map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                    <option value="custom">+ Add Custom / Other...</option>
                  </select>

                  {/* CONDITIONAL TEXT INPUT FIELD FOR CUSTOM TAGS */}
                  {showCustomInput && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px', animation: 'fadeIn 0.2s ease' }}>
                      <input 
                        type="text" 
                        placeholder="Enter your custom discipline..." 
                        className="setting-select" 
                        style={{ flex: 1, margin: 0 }}
                        value={customTagInput}
                        onChange={(e) => setCustomTagInput(e.target.value)}
                        maxLength={100} 
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleAddCustomTagSubmit(e);
                          }
                        }}
                      />
                      <button 
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAddCustomTagSubmit}
                        style={{ padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        Add
                      </button>
                    </div>
                  )}

                  {showCustomInput && (
                    <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '4px' }}>
                      * Custom disciplines are reviewed by moderators. Max 100 characters.
                    </div>
                  )}

                </div>
              </div>

              <div className="setting-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                <div className="sr-info" style={{ width: '100%' }}>
                  <div className="sr-label">Location</div>
                  <input 
                    type="text" 
                    className="setting-select" 
                    style={{ width: '100%', marginTop: '6px' }}
                    value={formData.location_text}
                    onChange={(e) => setFormData({...formData, location_text: e.target.value})}
                    placeholder="e.g. Nairobi, Kenya"
                  />
                </div>
              </div>

              <div className="setting-row">
                <button 
                  className="btn btn-primary" 
                  style={{ width: '100%', justifyContent: 'center', marginTop: '10px' }}
                  onClick={handleSaveProfile}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Profile Changes'}
                </button>
              </div>

              

              <div style={{ height: '1px', background: 'var(--border)', margin: '20px 0' }}></div>

              <div className="setting-row">
                <div className="sr-info"><div className="sr-label">Email</div><div className="sr-desc">{user?.email}</div></div>
              </div>
              <div className="setting-row">
                <div className="sr-info"><div className="sr-label">Deactivate account</div><div className="sr-desc">Temporarily hide your profile</div></div>
                <button className="btn btn-sm" style={{ borderColor: 'rgba(240,165,0,.4)', color: 'var(--amber)' }} onClick={() => showToast('Deactivation requires confirmation email')}>Deactivate</button>
              </div>
            </div>
          )}
          

          {/* ... Keep the rest of your conditional setting tabs (Notifications, Content, Blocked, Data, Appearance, About) exactly as they were in your code ... */}
          

      {activeTab === 'appearance' && (
        <div className="settings-section active" id="settings-appearance">
          <div className="ss-title">Appearance</div>
          <div className="ss-sub">Customise how Buniverse looks for you.</div>
          
          <div className="setting-row">
            <div className="sr-info">
              <div className="sr-label">Theme</div>
            </div>
            
            {/* 👉 Bind the select value and onChange to the hook we just added */}
            <select 
              className="setting-select" 
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
            >
              <option value="dark">Dark (default)</option>
              <option value="light">Light</option>
              <option value="system">System</option>
            </select>
            
          </div>             
        </div>
      )}

          {/* CONTENT SETTINGS - RESTORED UI */}
          {activeTab === 'content' && (
            <div className="settings-section active" id="settings-content">
              <div className="ss-title">Content settings</div>
              <div className="ss-sub">Manage how your content is displayed and shared.</div>

              {/* NEW: PORTFOLIO HIGHLIGHTS UPLOADER */}
              <div className="setting-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', paddingBottom: '20px', borderBottom: '1px solid var(--border)', marginBottom: '20px' }}>
                <div className="sr-info" style={{ width: '100%' }}>
                  <div className="sr-label">Portfolio Highlights (Max 6)</div>
                  <div className="sr-desc">Add links to your best work. We'll automatically generate the thumbnail.</div>
                  
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <select 
                      className="setting-select" 
                      style={{ flex: 1 }}
                      value={highlightInput.platform}
                      onChange={(e) => setHighlightInput({...highlightInput, platform: e.target.value})}
                    >
                      <option value="youtube">YouTube</option>
                      <option value="tiktok">TikTok</option>
                      <option value="behance">Behance</option>
                      <option value="instagram">Instagram</option>
                      <option value="other">Other Link</option>
                    </select>
                    
                    <input 
                      type="text" 
                      className="setting-select" 
                      style={{ flex: 3 }}
                      placeholder="Paste your video or project link here..."
                      value={highlightInput.url}
                      onChange={(e) => setHighlightInput({...highlightInput, url: e.target.value})}
                    />
                  </div>
                  
                  <button 
                    className="btn btn-primary" 
                    style={{ marginTop: '12px' }}
                    onClick={handleAddHighlight}
                    disabled={isSavingHighlight}
                  >
                    {isSavingHighlight ? 'Scraping Thumbnail...' : 'Add Highlight'}
                  </button>
                </div>
              </div>

              {/* NEW: PRESS ARTICLES UPLOADER */}
              <div className="setting-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', paddingBottom: '20px', borderBottom: '1px solid var(--border)', marginBottom: '20px' }}>
                <div className="sr-info" style={{ width: '100%' }}>
                  <div className="sr-label">Press Articles</div>
                  <div className="sr-desc">Feature articles, interviews, or features about your work.</div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                    <input type="text" className="setting-select" placeholder="Article Title" value={pressData.title} onChange={e => setPressData({...pressData, title: e.target.value})} />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input type="text" className="setting-select" style={{ flex: 1 }} placeholder="Publisher (e.g. Design Week)" value={pressData.publisher} onChange={e => setPressData({...pressData, publisher: e.target.value})} />
                      <input type="text" className="setting-select" style={{ flex: 1 }} placeholder="Date (e.g. March 2026)" value={pressData.publish_date} onChange={e => setPressData({...pressData, publish_date: e.target.value})} />
                    </div>
                    <input type="url" className="setting-select" placeholder="Article URL (https://...)" value={pressData.link_url} onChange={e => setPressData({...pressData, link_url: e.target.value})} />
                  </div>
                  
                  <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={handleAddPress} disabled={isSavingPress}>
                    {isSavingPress ? 'Saving...' : 'Add Press Link'}
                  </button>
                </div>
              </div>

              {/* NEW: AWARDS UPLOADER */}
              <div className="setting-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', paddingBottom: '20px', borderBottom: '1px solid var(--border)', marginBottom: '20px' }}>
                <div className="sr-info" style={{ width: '100%' }}>
                  <div className="sr-label">Awards & Recognitions</div>
                  <div className="sr-desc">Showcase your industry achievements.</div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                    <input type="text" className="setting-select" placeholder="Award Title" value={awardData.award_title} onChange={e => setAwardData({...awardData, award_title: e.target.value})} />
                    <input type="text" className="setting-select" placeholder="Organization" value={awardData.organization} onChange={e => setAwardData({...awardData, organization: e.target.value})} />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input type="text" className="setting-select" style={{ flex: 1 }} placeholder="Year" value={awardData.award_year} onChange={e => setAwardData({...awardData, award_year: e.target.value})} />
                      <input type="text" className="setting-select" style={{ flex: 1 }} placeholder="Tier (e.g. Gold, Winner)" value={awardData.award_tier} onChange={e => setAwardData({...awardData, award_tier: e.target.value})} />
                    </div>
                  </div>
                  
                  <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={handleAddAward} disabled={isSavingAward}>
                    {isSavingAward ? 'Saving...' : 'Add Award'}
                  </button>
                </div>
              </div>

              {/* NEW: UPCOMING EVENTS UPLOADER */}
              <div className="setting-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', paddingBottom: '20px', borderBottom: '1px solid var(--border)', marginBottom: '20px' }}>
                <div className="sr-info" style={{ width: '100%' }}>
                  <div className="sr-label">Upcoming Events</div>
                  <div className="sr-desc">Workshops, meetups, or live streams you are hosting.</div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                    <input type="text" className="setting-select" placeholder="Event Name (e.g. Brand Identity Workshop)" value={eventData.event_name} onChange={e => setEventData({...eventData, event_name: e.target.value})} />
                    
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {/* Using datetime-local ensures it formats perfectly for PostgreSQL timestamps */}
                      <input type="datetime-local" className="setting-select" style={{ flex: 1 }} value={eventData.event_date} onChange={e => setEventData({...eventData, event_date: e.target.value})} />
                      <input type="text" className="setting-select" style={{ flex: 1 }} placeholder="Location (e.g. The Hub or Zoom Link)" value={eventData.location} onChange={e => setEventData({...eventData, location: e.target.value})} />
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <select className="setting-select" style={{ flex: 1 }} value={eventData.is_free} onChange={e => setEventData({...eventData, is_free: e.target.value === 'true'})}>
                        <option value="true">Free Entry</option>
                        <option value="false">Paid / Ticketed</option>
                      </select>
                      <input type="url" className="setting-select" style={{ flex: 2 }} placeholder="Ticket / RSVP URL (Optional)" value={eventData.ticket_url} onChange={e => setEventData({...eventData, ticket_url: e.target.value})} />
                    </div>
                  </div>
                  
                  <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={handleAddEvent} disabled={isSavingEvent}>
                    {isSavingEvent ? 'Saving...' : 'Add Event'}
                  </button>
                </div>
              </div>
              
            </div>
          )}

          {/* PLATFORM CONNECTIONS - MOVED TO ITS OWN TAB */}
          {activeTab === 'platforms' && (
            <div className="settings-section active" id="settings-platforms">
              <div className="ss-title">Platform Connections</div>
              <div className="ss-sub">Connect your professional creative profiles. Add handles or full urls.</div>

              {['instagram', 'youtube', 'behance', 'dribbble', 'spotify', 'twitter'].map((platform) => (
                <div className="setting-row" key={platform} style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                  <div className="sr-info" style={{ width: '100%' }}>
                    <div className="sr-label" style={{ textTransform: 'capitalize', fontWeight: 600 }}>
                      {platform === 'twitter' ? 'X / Twitter' : platform}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                      {/* URL INPUT */}
                      <input 
                        type="text" 
                        className="setting-select" 
                        style={{ flex: 2 }}
                        placeholder={`Enter ${platform} link`}
                        value={platforms[platform]?.url || ''}
                        onChange={(e) => setPlatforms({ 
                          ...platforms, 
                          [platform]: { ...platforms[platform], url: e.target.value } 
                        })}
                      />
                      
                      {/* FOLLOWER COUNT INPUT */}
                      <input 
                        type="number" 
                        className="setting-select" 
                        style={{ flex: 1 }}
                        placeholder="Followers (e.g. 1500)"
                        value={platforms[platform]?.followers || ''}
                        onChange={(e) => setPlatforms({ 
                          ...platforms, 
                          [platform]: { ...platforms[platform], followers: e.target.value } 
                        })}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="setting-row">
                <button 
                  className="btn btn-primary" 
                  style={{ width: '100%', justifyContent: 'center', marginTop: '12px' }}
                  onClick={handleSaveLinks}
                  disabled={isSavingPlatforms}
                >
                  {isSavingPlatforms ? 'Saving Connections...' : 'Save Connections'}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}